import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
} from "@mui/material";
import axios from "axios";
import { Formik } from "formik";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import api from "../../services/BackendServices";

const AuthRegister = () => {
  const [loading, setLoading] = React.useState(false);
  const navigate = useNavigate();
  const [error, setError] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [emailSent, setEmailSent] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [countries, setCountries] = React.useState([]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const handleMouseDownPassword = (event: any) => {
    event.preventDefault();
  };
  const [showPasswordConfirm, setShowPasswordConfirm] = React.useState(false);
  const handleClickShowPasswordConfirm = () => {
    setShowPasswordConfirm(!showPasswordConfirm);
  };
  const handleMouseDownPasswordConfirm = (event: any) => {
    event.preventDefault();
  };

  const handleCancel = () => {
    navigate("/");
  };

  const validations = Yup.object().shape({
    realm: Yup.string()
      .max(255, "Máximo de 255 caracteres.")
      .required("Ingrese el nombre y apellido."),
    email: Yup.string()
      .email("Tiene que ingresar un mail correcto.")
      .max(255, "Máximo de 255 caracteres.")
      .required("El mail es requerido."),
    dateOfBirth: Yup.date()
      .max(
        new Date(),
        "La fecha de nacimiento no puede ser mayor al día de hoy."
      )
      .required("Ingrese su fecha de nacimiento."),
    country: Yup.string()
      .max(255, "Máximo de 255 caracteres.")
      .required("Ingrese el país."),
    city: Yup.string()
      .max(255, "Máximo de 255 caracteres.")
      .required("Ingrese la ciudad."),
    profession: Yup.string()
      .max(255, "Máximo de 255 caracteres.")
      .required("Ingrese la ciudad."),
    professionalEstablishment: Yup.string().max(
      255,
      "Máximo de 255 caracteres."
    ),
    password: Yup.string()
      .max(255, "Máximo de 255 caracteres.")
      .required("Ingrese la contraseña.")
      .min(8, "La contraseña debe tener 8 o más caracteres."),
    passwordConfirm: Yup.string()
      .max(255, "Máximo de 255 caracteres.")
      .oneOf([Yup.ref("password")], "Las contraseñas deben coincidir.")
      .required("Debe confirmar la contraseña.")
      .min(8, "La contraseña debe tener 8 o más caracteres."),
    termsAndConditions: Yup.boolean()
      .required("Debes aceptar los términos y condiciones.")
      .oneOf([true], "Debes aceptar los términos y condiciones."),
  });

  const register = async (
    values: any,
    { setErrors, setStatus, setSubmitting }: any
  ) => {
    setLoading(true);
    setError("");
    try {
      setEmail(values.email);

      api.SecurityServices.register({
        earlyAdopter: {
          name: values.realm,
          password: values.password,
          email: values.email,
          country: values.country,
          dateOfBirth: values.dateOfBirth,
          profession: values.profession,
          professionalEstablishment: values.professionalEstablishment,
          city: values.city,
          applicationId: 1, //Este id sale de la tabla Applications, es el registro de Trixie
        },
        termsAndConditionsAudit: {
          agree: true,
          date: new Date(),
          file: process.env.REACT_APP_TERMS_AND_CONDITION,
        },
      })
        .then((response) => {
          setEmailSent(true);
          setStatus({ success: true });
          setSubmitting(false);
        })
        .catch(({ response }) => {
          setError(response.data.error.message);
        });
    } catch (err: any) {
      setStatus({ success: false });
      setErrors({ submit: err.message });
      setSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Llamada a la API de REST Countries
    axios
      .get("https://restcountries.com/v3.1/all")
      .then((response) => {
        const countryData = response.data
          .map((country) => ({
            name: country.translations.spa.common,
            code: country.translations.spa.common,
          }))
          .sort((a, b) => a.name.localeCompare(b.name)); // Orden ascendente por nombre

        setCountries(countryData);
      })
      .catch((error) => {
        console.error("Error fetching countries:", error);
      });
  }, []);

  return (
    <div>
      {emailSent && (
        <Dialog
          open={emailSent}
          onClose={() => {
            setEmailSent(false);
            navigate("/");
          }}
        >
          <DialogTitle>Registro Exitoso</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {/* Se ha enviado un correo a {email}. Por favor, verifica tu bandeja
              de entrada y sigue los pasos para completar tu registro. */}
              Nuestro equipo se encuentra procesando la solicitud. En cuanto el
              proceso finalice se enviará un correo a {email}. Por favor,
              verifica tu bandeja de entrada y sigue los pasos para completar tu
              registro.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setEmailSent(false);
                navigate("/");
              }}
              color="primary"
            >
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {error && (
        <Alert style={{ marginBottom: "10px" }} severity="error">
          {error}
        </Alert>
      )}
      <Formik
        initialValues={{
          realm: "",
          email: "",
          country: "",
          city: "",
          dateOfBirth: null,
          profession: "",
          professionalEstablishment: "",
          termsAndConditions: false,
          password: "",
          passwordConfirm: "",
          submit: null,
        }}
        validationSchema={validations}
        onSubmit={register}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Grid
              container
              style={{
                width: "100%",
                marginLeft: 0,
                display: "flex",
                flexDirection: "column",
                gap: "1.5rem",
              }}
            >
              <div style={{ display: "flex", gap: "1rem" }}>
                <Grid item xs={6} style={{ paddingLeft: 0 }}>
                  <Stack spacing={1}>
                    <InputLabel
                      style={{ color: "var(--white)" }}
                      htmlFor="realm-register"
                    >
                      Nombre y Apellido *
                    </InputLabel>
                    <OutlinedInput
                      id="realm-register"
                      type="realm"
                      style={{
                        color: "var(--white)",
                        height: "55px",
                        border: "1px solid gray",
                      }}
                      value={values.realm}
                      name="realm"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="Nombre y Apellido"
                      fullWidth
                      error={Boolean(touched.realm && errors.realm)}
                    />

                    {touched.realm && errors.realm && (
                      <FormHelperText
                        error
                        id="standard-weight-helper-text-realm-register"
                      >
                        {errors.realm.toString()}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={6} style={{ paddingLeft: 0 }}>
                  <Stack spacing={1}>
                    <InputLabel
                      style={{ color: "var(--white)" }}
                      htmlFor="email-register"
                    >
                      Email *
                    </InputLabel>
                    <OutlinedInput
                      id="email-register"
                      type="email"
                      style={{
                        color: "var(--white)",
                        height: "55px",
                        border: "1px solid gray",
                      }}
                      value={values.email}
                      name="email"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="mail@mail.com"
                      fullWidth
                      error={Boolean(touched.email && errors.email)}
                    />
                    {touched.email && errors.email && (
                      <FormHelperText
                        error
                        id="standard-weight-helper-text-email-register"
                      >
                        {errors.email.toString()}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
              </div>
              <div style={{ display: "flex", gap: "1rem" }}>
                <Grid item xs={4} style={{ paddingLeft: 0 }}>
                  <Stack spacing={1}>
                    <InputLabel
                      style={{ color: "var(--white)" }}
                      htmlFor="dateOfBirth"
                    >
                      Fecha de nacimiento *
                    </InputLabel>
                    <OutlinedInput
                      id="dateOfBirth"
                      type="date"
                      style={{
                        color: "var(--white)",
                        height: "55px",
                        border: "1px solid gray",
                      }}
                      value={values.dateOfBirth}
                      name="dateOfBirth"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      fullWidth
                      inputProps={{
                        max: new Date().toISOString().split("T")[0], // Establecer la fecha máxima
                      }}
                      error={Boolean(touched.dateOfBirth && errors.dateOfBirth)}
                    />
                    {touched.dateOfBirth && errors.dateOfBirth && (
                      <FormHelperText
                        error
                        id="standard-weight-helper-text-email-register"
                      >
                        {errors.dateOfBirth.toString()}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={4} style={{ paddingLeft: 0 }}>
                  <Stack spacing={1}>
                    <InputLabel
                      style={{ color: "var(--white)" }}
                      htmlFor="country"
                    >
                      País *
                    </InputLabel>
                    <Select
                      id="country"
                      value={values.country}
                      name="country"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="Seleccionar país..."
                      fullWidth
                      error={Boolean(touched.country && errors.country)}
                      style={{
                        color: "var(--white)",
                        height: "55px",
                        border: "1px solid gray",
                      }}
                    >
                      {countries.map((country: any) => (
                        <MenuItem key={country.code} value={country.name}>
                          {country.name}
                        </MenuItem>
                      ))}
                    </Select>

                    {touched.country && errors.country && (
                      <FormHelperText
                        error
                        id="standard-weight-helper-text-realm-register"
                      >
                        {errors.country.toString()}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={4} style={{ paddingLeft: 0 }}>
                  <Stack spacing={1}>
                    <InputLabel
                      style={{ color: "var(--white)" }}
                      htmlFor="city"
                    >
                      Ciudad *
                    </InputLabel>
                    <OutlinedInput
                      id="city"
                      type="text"
                      style={{
                        color: "var(--white)",
                        height: "55px",
                      }}
                      value={values.city}
                      name="city"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="Ciudad..."
                      fullWidth
                      error={Boolean(touched.city && errors.city)}
                      sx={{
                        color: "var(--white)",
                        height: "55px",
                        backgroundColor: "transparent",
                        border: "none",
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                      }}
                    />
                    {touched.city && errors.city && (
                      <FormHelperText
                        error
                        id="standard-weight-helper-text-email-register"
                      >
                        {errors.city.toString()}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
              </div>
              <div style={{ display: "flex", gap: "1rem" }}>
                {" "}
                <Grid item xs={6} style={{ paddingLeft: 0 }}>
                  <Stack spacing={1}>
                    <InputLabel
                      style={{ color: "var(--white)" }}
                      htmlFor="profession"
                    >
                      Ocupación *
                    </InputLabel>
                    <OutlinedInput
                      id="profession"
                      type="text"
                      style={{
                        color: "var(--white)",
                        height: "55px",
                      }}
                      value={values.profession}
                      name="profession"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="Ocupación..."
                      fullWidth
                      error={Boolean(touched.profession && errors.profession)}
                      sx={{
                        color: "var(--white)",
                        height: "55px",
                        backgroundColor: "transparent",
                        border: "none",
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                      }}
                    />

                    {touched.profession && errors.profession && (
                      <FormHelperText
                        error
                        id="standard-weight-helper-text-realm-register"
                      >
                        {errors.profession.toString()}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={6} style={{ paddingLeft: 0 }}>
                  <Stack spacing={1}>
                    <InputLabel
                      style={{ color: "var(--white)" }}
                      htmlFor="professionalEstablishment"
                    >
                      Empresa/Institución
                    </InputLabel>
                    <OutlinedInput
                      id="professionalEstablishment"
                      type="text"
                      style={{
                        color: "var(--white)",
                        height: "55px",
                      }}
                      value={values.professionalEstablishment}
                      name="professionalEstablishment"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="Establecimiento..."
                      fullWidth
                      error={Boolean(
                        touched.professionalEstablishment &&
                          errors.professionalEstablishment
                      )}
                      sx={{
                        color: "var(--white)",
                        height: "55px",
                        backgroundColor: "transparent",
                        border: "none",
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                      }}
                    />
                    {touched.professionalEstablishment &&
                      errors.professionalEstablishment && (
                        <FormHelperText
                          error
                          id="standard-weight-helper-text-email-register"
                        >
                          {errors.professionalEstablishment.toString()}
                        </FormHelperText>
                      )}
                  </Stack>
                </Grid>
              </div>
              <div style={{ display: "flex", gap: "1rem" }}>
                <Grid item xs={6} style={{ paddingLeft: 0 }}>
                  <Stack spacing={1}>
                    <InputLabel
                      style={{ color: "var(--white)" }}
                      htmlFor="password-register"
                    >
                      Contraseña *
                    </InputLabel>
                    <OutlinedInput
                      style={{
                        color: "var(--white)",
                        height: "55px",
                        border: "1px solid gray",
                      }}
                      fullWidth
                      error={Boolean(touched.password && errors.password)}
                      id="-password-register"
                      type={showPassword ? "text" : "password"}
                      value={values.password}
                      name="password"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                            size="large"
                          >
                            {showPassword ? (
                              <EyeOutlined style={{ color: "white " }} />
                            ) : (
                              <EyeInvisibleOutlined
                                style={{ color: "white" }}
                              />
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                      placeholder="Contraseña..."
                    />
                    {touched.password && errors.password && (
                      <FormHelperText
                        error
                        id="standard-weight-helper-text-password-register"
                      >
                        {errors.password.toString()}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={6} style={{ paddingLeft: 0 }}>
                  <Stack spacing={1}>
                    <InputLabel
                      style={{ color: "var(--white)" }}
                      htmlFor="passwordConfirm-register"
                    >
                      Confirmar contraseña *
                    </InputLabel>
                    <OutlinedInput
                      style={{
                        color: "var(--white)",
                        height: "55px",
                        border: "1px solid gray",
                      }}
                      fullWidth
                      error={Boolean(
                        touched.passwordConfirm && errors.passwordConfirm
                      )}
                      id="-passwordConfirm-register"
                      type={showPasswordConfirm ? "text" : "password"} // Línea corregida
                      value={values.passwordConfirm}
                      name="passwordConfirm"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle passwordConfirm visibility"
                            onClick={handleClickShowPasswordConfirm}
                            onMouseDown={handleMouseDownPasswordConfirm}
                            edge="end"
                            size="large"
                          >
                            {showPasswordConfirm ? (
                              <EyeOutlined style={{ color: "white" }} />
                            ) : (
                              <EyeInvisibleOutlined
                                style={{ color: "white" }}
                              />
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                      placeholder="Contraseña..."
                    />

                    {touched.passwordConfirm && errors.passwordConfirm && (
                      <FormHelperText
                        error
                        id="standard-weight-helper-text-passwordConfirm-register"
                      >
                        {errors.passwordConfirm.toString()}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
              </div>
              <Grid item xs={12} style={{ paddingLeft: 0 }}>
                <Stack spacing={1}>
                  <FormControl
                    error={Boolean(
                      touched.termsAndConditions && errors.termsAndConditions
                    )}
                  >
                    <FormControlLabel
                      label={
                        <>
                          Acepto los{" "}
                          <Link
                            href={process.env.REACT_APP_TERMS_AND_CONDITION_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Términos y Condiciones
                          </Link>
                        </>
                      }
                      id="termsAndConditions"
                      control={
                        <Checkbox
                          color="primary"
                          sx={{
                            color: `${
                              touched.termsAndConditions &&
                              errors.termsAndConditions
                                ? "error.main"
                                : "gray"
                            }`,
                            marginLeft: "-6px",
                          }}
                          name="termsAndConditions"
                          value={values.termsAndConditions}
                          onChange={handleChange}
                        />
                      }
                    />
                    {touched.termsAndConditions &&
                      errors.termsAndConditions && (
                        <FormHelperText
                          error
                          id="standard-weight-helper-text-email-register"
                        >
                          {errors.termsAndConditions.toString()}
                        </FormHelperText>
                      )}
                  </FormControl>
                </Stack>
              </Grid>
              <Grid
                item
                xs={12}
                style={{
                  marginTop: "1rem",
                  paddingLeft: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <Button
                  disableElevation
                  disabled={loading}
                  size="medium"
                  variant="contained"
                  style={{
                    background: "var(--color-red)",
                    color: "white",
                    borderRadius: "5px",
                    padding: "10px",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    textTransform: "none",
                  }}
                  onClick={handleCancel}
                >
                  CANCELAR
                </Button>
                <Button
                  disableElevation
                  disabled={loading}
                  size="medium"
                  variant="contained"
                  style={{
                    background: "var(--color-primary)",
                    color: "white",
                    borderRadius: "5px",
                    padding: "10px",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    textTransform: "none",
                  }}
                  onClick={() => handleSubmit()}
                >
                  REGISTRAR
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default AuthRegister;
